import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { routeTree } from './routeTree.gen';
import './index.css';
import './i18n';
import { getThemeParams } from '@/lib/telegram';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { queryClient } from '@/lib/query-client';
import { api } from '@/lib/api';

// Phase 4 P3-22: Sentry no-op без VITE_SENTRY_DSN.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENV || 'dev',
    release: import.meta.env.VITE_APP_VERSION as string | undefined,
    tracesSampleRate: 0.1,
  });
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadDelay: 50,
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const setTgThemeVariables = () => {
  const theme = getThemeParams();
  if (!theme.bg_color) return;
  const root = document.documentElement;
  const set = (key: string, value: string | undefined) => {
    if (value) root.style.setProperty(key, value);
  };
  set('--tg-theme-bg-color', theme.bg_color);
  set('--tg-theme-text-color', theme.text_color);
  set('--tg-theme-hint-color', theme.hint_color);
  set('--tg-theme-link-color', theme.link_color);
  set('--tg-theme-button-color', theme.button_color);
  set('--tg-theme-button-text-color', theme.button_text_color);
  set('--tg-theme-secondary-bg-color', theme.secondary_bg_color);
};

setTgThemeVariables();

// Prefetch главного запроса для / (Platform dashboard).
queryClient
  .prefetchQuery({ queryKey: ['partners'], queryFn: () => api.partners() })
  .catch(() => {/* fallback on mount */});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>,
);
